import type { NarrativeContext, NarrativeResult } from "../../narrative";
import type {
  LifeRecord,
  EarthDimension,
  EarthRow,
  EarthDivergence,
  LifeBeat,
  LifeCareerStage,
  LifeFuturePath,
  LifeTimelineEntry,
} from "../../types";
import { ENGINE_VERSION } from "../../lifeChapters";

const EARTH_DIMENSIONS: EarthDimension[] = [
  "Occupation",
  "Social status",
  "Financial circumstances",
  "Relationships",
  "Freedoms",
  "Daily life",
  "Available technology",
  "Future opportunities",
];

/**
 * Builds a deterministic, minimal but complete fallback LifeRecord when narrative generators throw
 * after exhausted retry attempts (>= 8). No Math.random; all 13 chapters are fully populated.
 */
export function buildFallbackLife(
  ctx: NarrativeContext,
  base: NarrativeResult
): LifeRecord {
  const name = base.name || "Traveler";
  const occupation = base.occupation || "Explorer";
  const universeName = ctx.universe?.name || "Alternate Cosmos";
  const dilemma = base.dossier?.dilemma || "Navigating uncharted paths across diverging realities.";
  const origin = base.story?.origin || "Raised within an alternate stream of history.";
  const turningPoint = base.story?.turningPoint || "A decisive choice altered their life trajectory.";
  const today = base.story?.today || "Active within their community, pursuing their core calling.";
  const personality = base.dossier?.personality || "Reflective, resourceful, and committed to their path.";
  const skills = base.dossier?.skills?.length
    ? base.dossier.skills
    : ["Systematic analysis", "Adaptive communication", "Strategic planning", "Applied craftsmanship"];
  const achievements = base.dossier?.achievements?.length
    ? base.dossier.achievements
    : ["Established key institutional processes", "Preserved vital community knowledge"];
  const failures = base.dossier?.failures?.length
    ? base.dossier.failures
    : ["An early project faltered from premature expansion.", "A pivotal partnership dissolved over conflicting ethics."];

  const age = 34 + (Math.abs(ctx.seed) % 25);
  const influenceScore = Math.min(95, Math.max(20, Math.round(ctx.axes?.influence ?? 50)));

  const biography = [
    `${name} lives as ${occupation.toLowerCase()} within ${universeName}. Born into a world shaped by distinct civic and technological assumptions, their early years reflected the core values of their environment.`,
    `${origin} The critical shift occurred when ${turningPoint.toLowerCase().replace(/^\w/, (c) => c.toLowerCase())}`,
    `Today, ${today} Recognized for ${personality.toLowerCase()} they maintain an influential standing among their peers, balancing demanding institutional responsibilities with personal principles.`,
  ];

  const careerHistory: LifeCareerStage[] = [
    {
      stage: "Apprenticeship & Foundation",
      years: "Early career",
      text: `Trained under seasoned practitioners, mastering the fundamental techniques and operational standards of ${occupation.toLowerCase()}.`,
    },
    {
      stage: "Independent Practice",
      years: "Mid career",
      text: `Assumed direct operational leadership, establishing a distinct voice and leading significant regional initiatives.`,
    },
    {
      stage: "Established Seniority",
      years: "Current tenure",
      text: `Coordinates complex operations, guides rising practitioners, and addresses systemic challenges in ${universeName}.`,
    },
  ];

  const responsibilities = [
    `Overseeing core daily operations within ${occupation.toLowerCase()} domain`,
    `Evaluating strategic proposals and ensuring compliance with sector standards`,
    `Mentoring emerging specialists and preserving institutional wisdom`,
    `Balancing long-term objectives against immediate community needs`,
  ];

  const possessions = [
    "Registered certification insignia and professional credentials",
    "Tailored field equipment and specialized computational tools",
    "Personal library of technical treatises and civic archives",
    "Heirloom travel gear adapted for local transit networks",
  ];

  const routine = [
    "Dawn review of regional notifications and incoming briefs",
    "Morning coordination with working groups and field operatives",
    "Afternoon dedicated to intensive analytical or creative execution",
    "Evening synthesis, journal documentation, and family meal",
  ];

  const beats: LifeBeat[] = [
    {
      time: "06:30",
      heading: "Morning Awakening & Assessment",
      text: `${name} begins the morning reviewing local environmental metrics and prioritizing key commitments for the working day.`,
    },
    {
      time: "09:00",
      heading: "Operational Directives",
      text: `Arrives at the workplace to convene with core team members, addressing operational friction and aligning priorities.`,
    },
    {
      time: "13:30",
      heading: "Execution & Collaboration",
      text: `Devotes mid-day hours to direct technical engagement, refining models and resolving intricate domain problems.`,
    },
    {
      time: "18:00",
      heading: "Civic Engagement & Solitude",
      text: `Steps away from official duties for unhurried conversations with peers, followed by a quiet walk through the district.`,
    },
    {
      time: "21:30",
      heading: "Evening Reflection",
      text: `Records day-end insights, maintains personal records, and rests before the requirements of the next cycle.`,
    },
  ];

  const futures: LifeFuturePath[] = [
    {
      id: "a",
      name: "Direct Institutional Leadership",
      flavor: "Most natural",
      summary: `Natural expansion of current responsibilities leading to senior directorship in ${universeName}.`,
      opportunities: ["Broad administrative reach", "Ability to institutionalize lasting standards", "Substantial resource allocation"],
      challenges: ["Entrenched bureaucracy", "Reduced time for hands-on creative practice", "Heightened public scrutiny"],
      consequences: `Solidifies long-term structural impact across the sector while cementing ${name}'s historical legacy.`,
    },
    {
      id: "b",
      name: "Trans-Regional Pioneer",
      flavor: "Ambitious",
      summary: `Departing the established center to establish groundbreaking methodologies across frontier territories.`,
      opportunities: ["Unprecedented creative autonomy", "First-hand exploration of nascent territories", "Transformative discoveries"],
      challenges: ["Unstable supply lines", "Absence of established peer support", "Elevated physical and material risks"],
      consequences: `Redefines the boundaries of the discipline, opening entirely new avenues for future generations.`,
    },
    {
      id: "c",
      name: "Philosophical Synthesis & Solitude",
      flavor: "Wildcard",
      summary: `Stepping back from high-visibility roles to author definitive treatises synthesizing decades of practice.`,
      opportunities: ["Immense intellectual freedom", "Clarity of purpose", "Unfiltered philosophical expression"],
      challenges: ["Diminished immediate societal influence", "Financial re-adjustment", "Professional isolation"],
      consequences: `Leaves behind an enduring body of thought that outlives transient political and social structures.`,
    },
  ];

  const timeline: LifeTimelineEntry[] = [
    {
      age: 12,
      label: "Early Discovery",
      title: "First Spark of Calling",
      text: `Encountered foundational mentors and materials that first ignited deep fascination with their future domain.`,
      kind: "origin",
    },
    {
      age: 20,
      label: "Formative Milestone",
      title: "Formal Certification",
      text: `Completed rigorous academic and vocational preparation, establishing their official credentials.`,
      kind: "education",
    },
    {
      age: 26,
      label: "Decisive Choice",
      title: "The Crossroads",
      text: turningPoint,
      kind: "choice",
    },
    {
      age: 31,
      label: "Major Landmark",
      title: "Breakthrough Achievement",
      text: achievements[0] ?? "Earned broad recognition through exceptional technical execution.",
      kind: "achievement",
    },
    {
      age,
      label: "Current Standpoint",
      title: "Present Dilemma",
      text: dilemma,
      kind: "present",
    },
    {
      age: age + 8,
      label: "Approaching Horizon",
      title: "Prospective Transition",
      text: `Prepared to initiate a major structural evolution across their broader sphere of influence.`,
      kind: "future",
    },
  ];

  const divergences: EarthDivergence[] = [
    {
      id: "d1",
      title: "Divergence in Foundational Institutions",
      text: `In this reality, foundational education and resource access are organized around cooperative guilds rather than speculative market forces.`,
      effects: `Guarantees stability and long-term mentorship, shifting individual focus from survival to sustained mastery.`,
    },
    {
      id: "d2",
      title: "Technological & Environmental Paradigm",
      text: `Scientific evolution within ${universeName} prioritized ecological integration and resilient infrastructure over rapid obsolescence.`,
      effects: `Reduces day-to-day material volatility while elevating craftsmanship and longevity.`,
    },
  ];

  const rows: EarthRow[] = EARTH_DIMENSIONS.map((dim, idx) => {
    switch (dim) {
      case "Occupation":
        return {
          dimension: dim,
          here: occupation,
          earth: "Conventional contemporary profession",
          because: `Divergent civic priorities in ${universeName} created formal institutional demand for ${occupation.toLowerCase()}.`,
          divergenceId: "d1",
        };
      case "Social status":
        return {
          dimension: dim,
          here: "Respected senior practitioner in cooperative networks",
          earth: "Hierarchical corporate or administrative standing",
          because: "Social credit in this timeline derives from verified technical contributions rather than capital accumulation.",
          divergenceId: "d1",
        };
      case "Financial circumstances":
        return {
          dimension: dim,
          here: "Stable equity in civic assets and mutualist credit",
          earth: "Private wage dependency and market-driven living costs",
          because: "Essential utilities and living spaces are insulated from private speculation by mutualist compacts.",
          divergenceId: "d2",
        };
      case "Relationships":
        return {
          dimension: dim,
          here: "Dense guild solidarity and enduring collaborative bonds",
          earth: "Geographically dispersed personal and professional contacts",
          because: "Civic architecture actively cultivates localized, multi-generational community pods.",
          divergenceId: "d1",
        };
      case "Freedoms":
        return {
          dimension: dim,
          here: "High autonomy in work methods, guided by collective standards",
          earth: "Regulated corporate compliance and external metric evaluation",
          because: "Institutional trust is vested in accredited practitioners rather than external oversight bodies.",
          divergenceId: "d1",
        };
      case "Daily life":
        return {
          dimension: dim,
          here: "Structured cadence aligned with natural light and community rhythms",
          earth: "Paced by synchronized digital connectivity and commuting demands",
          because: "Urban and transport layout centers on compact districts and active transit systems.",
          divergenceId: "d2",
        };
      case "Available technology":
        return {
          dimension: dim,
          here: "Repairable, modular tools engineered for multi-decade durability",
          earth: "Rapid consumer iterations dependent on centralized cloud platforms",
          because: "Technological development emphasizes local resilience and user repairability.",
          divergenceId: "d2",
        };
      case "Future opportunities":
        return {
          dimension: dim,
          here: "Clear branching paths toward regional leadership or pure scholarship",
          earth: "Volatile career trajectories influenced by global economic shifts",
          because: "Institutional structures remain predictable, ensuring effort reliably yields recognized progression.",
          divergenceId: "d1",
        };
    }
  });

  const visitorSignals = [
    `Your selected interests provided the thematic grounding for ${name}'s expertise as ${occupation.toLowerCase()}.`,
    `Your stated strengths directly informed ${name}'s collaborative and decision-making style.`,
    `Your selected ambition established the trajectory that propelled this self through their defining turning points.`,
  ];

  return {
    engineVersion: ENGINE_VERSION,
    identity: {
      fullName: name,
      age,
      ageNote: "Standard solar calendar years measured in our physical universe.",
      personalitySummary: personality,
      biography,
    },
    work: {
      occupation,
      expertise: skills,
      careerHistory,
      responsibilities,
      workplace: `Regional operational center in ${universeName}`,
    },
    standing: {
      wealthBracket: "Comfortable / Stable",
      wealthText: `Holds secure equity in local working collectives and regional trade credits.`,
      socialClass: "Respected Practitioner",
      reputation: `Widely respected for technical dependability and calm, measured discernment.`,
      prestige: `Recognized across regional boards and active trade guilds.`,
      influence: `Direct voice in regional resource allocation and operational standards.`,
      influenceLevel: influenceScore,
      economicCircumstances: `Well insulated against speculative volatility by resilient municipal infrastructure.`,
    },
    home: {
      dwelling: `Well-appointed district residence near primary civic avenues`,
      environment: `Temperate urban district structured around communal courtyards and active walkways.`,
      lifestyle: `Balanced cadence centered on early focus, purposeful labor, and evening contemplation.`,
      possessions,
      transport: "Active municipal transit network and light personal transit vehicle.",
      routine,
    },
    people: {
      family: `Maintains close ties with extended family residing across adjacent districts.`,
      relationships: `Supported by an intimate circle of trusted colleagues and longstanding companions.`,
      friendships: `Deep bonds formed through shared creative initiatives and decades of mutual support.`,
      socialCircle: `Active presence within professional associations and civic deliberative forums.`,
    },
    formative: {
      childhood: origin.slice(0, 180) + (origin.length > 180 ? "..." : ""),
      education: `Comprehensive multi-disciplinary schooling paired with rigorous accredited mentorship.`,
      decisions: [
        `Committed to rigorous specialization early in vocational development.`,
        `Chose regional community engagement over distant institutional positions.`,
      ],
      achievements,
      setbacks: failures,
      turningPoints: [turningPoint],
    },
    society: {
      summary: `A structured civilization balancing personal agency with strong mutual responsibility.`,
      laws: `Civic legal code emphasizing equitable restitution and shared resource management.`,
      culture: `High cultural valuation of craftsmanship, philosophical debate, and public service.`,
      institutions: `Autonomous deliberative councils and accredited practitioner guilds.`,
      expectations: `Citizens are expected to contribute distinct competencies toward shared civic well-being.`,
      opportunity: `Accessible pathways for advancement based on demonstrated mastery and peer validation.`,
    },
    world: {
      technology: `Advanced modular engineering prioritizing longevity, energy efficiency, and repairability.`,
      science: `Empirical science closely harmonized with ecological and physical dynamics.`,
      geography: `Interconnected regional settlements surrounded by conserved natural reserves.`,
      history: `Centuries of steady consolidation following ancient periods of fragmentation.`,
      environment: `Carefully stewarded biosphere maintaining stable ecological equilibrium.`,
    },
    howDiffers: {
      headline: `A Reality Structured Around Mastered Purpose Rather Than Market Anxiety`,
      paragraphs: [
        `In ${universeName}, societal advancement is decoupled from speculative capital accumulation. Institutions are structured to reward mastery, dependability, and verified community contributions.`,
        `For ${name}, this manifests as an environment where professional practice is treated with dignity, and daily rhythms allow deep focus rather than perpetual digital distraction.`,
      ],
      keyDifferences: [
        {
          title: "Civic Stewardship",
          text: "Resources are managed cooperatively, reducing acute financial precarity across all strata.",
        },
        {
          title: "Longevity of Built Infrastructure",
          text: "Tools, dwellings, and systems are built to endure for generations without engineered obsolescence.",
        },
      ],
    },
    dayInLife: {
      title: "A Day in This Life",
      beats,
      closing: `${name} concludes the day with quiet satisfaction, having advanced meaningful commitments in harmony with their values.`,
    },
    futures: {
      title: "Three Possible Futures",
      paths: futures,
      disclaimer: "These forward trajectories represent speculative story branches based on current momentum, not immutable predictions.",
    },
    timeline,
    earthComparison: {
      basis: "general-reference",
      basisNote: "This Earth reference reflects typical modern conditions for comparison, because your questionnaire contained no private personal details.",
      rows,
      divergences,
      visitorSignals,
    },
  };
}

/**
 * Builds a 100% fail-safe minimal LifeRecord that CANNOT throw under any circumstances.
 * Uses only static text and safe optional chaining with defaults (?? "").
 * No pick(), no Math.random, all 13 chapters and sub-fields strictly populated.
 */
export function buildMinimalLife(
  ctx: NarrativeContext,
  base: NarrativeResult
): LifeRecord {
  const name = base?.name || "Traveler";
  const occupation = base?.occupation || "Explorer";
  const universeName = ctx?.universe?.name || "Alternate Cosmos";
  const dilemma = base?.dossier?.dilemma || "Balancing core principles against the unfolding demands of an alternate reality.";
  const origin = base?.story?.origin || "Raised within an alternate stream of history.";
  const turningPoint = base?.story?.turningPoint || "A decisive personal choice redirected their life trajectory.";
  const today = base?.story?.today || "Active within their community, pursuing their chosen craft.";
  const personality = base?.dossier?.personality || "Thoughtful, determined, and deeply committed to their daily work.";
  const skills = (base?.dossier?.skills && base.dossier.skills.length > 0)
    ? base.dossier.skills
    : ["Systematic analysis", "Adaptive communication", "Strategic planning", "Practical craftsmanship"];
  const achievements = (base?.dossier?.achievements && base.dossier.achievements.length > 0)
    ? base.dossier.achievements
    : ["Established vital community processes", "Preserved foundational knowledge"];
  const failures = (base?.dossier?.failures && base.dossier.failures.length > 0)
    ? base.dossier.failures
    : ["An early project suffered from premature expansion.", "A key initiative stalled due to resource shortages."];

  const seed = typeof ctx?.seed === "number" ? Math.abs(ctx.seed) : 100;
  const age = 34 + (seed % 25);
  const influenceScore = Math.min(95, Math.max(20, Math.round(ctx?.axes?.influence ?? 50)));

  const biography = [
    `${name} lives as ${occupation.toLowerCase()} within ${universeName}. Born into a world shaped by distinct civic and environmental conditions, their early life reflected the core assumptions of their culture.`,
    `${origin} The defining transformation occurred when ${turningPoint.toLowerCase().replace(/^\w/, (c) => c.toLowerCase())}`,
    `Today, ${today} Recognized for ${personality.toLowerCase()} they maintain a respected position among their contemporaries, balancing demanding responsibilities with individual convictions.`,
  ];

  const careerHistory: LifeCareerStage[] = [
    {
      stage: "Foundation & Apprenticeship",
      years: "Early career",
      text: `Acquired practical competencies and theoretical foundations under experienced mentors in ${occupation.toLowerCase()}.`,
    },
    {
      stage: "Active Practice",
      years: "Mid career",
      text: `Took on direct operational leadership and earned broad recognition across regional initiatives.`,
    },
    {
      stage: "Senior Leadership",
      years: "Current tenure",
      text: `Directs complex projects and guides the next generation of practitioners in ${universeName}.`,
    },
  ];

  const responsibilities = [
    `Overseeing core daily operations within the ${occupation.toLowerCase()} domain`,
    `Evaluating strategic decisions and maintaining professional standards`,
    `Guiding emerging practitioners and preserving institutional knowledge`,
    `Harmonizing long-term goals with urgent community priorities`,
  ];

  const possessions = [
    "Official credentials and professional insignia",
    "Specialized field instruments and analytical devices",
    "Curated collection of technical treatises and records",
    "Sturdy travel gear adapted for local transit networks",
  ];

  const routine = [
    "Morning assessment of regional communications and priority briefs",
    "Mid-day coordination with colleagues and active working groups",
    "Afternoon dedicated to focused creative or technical execution",
    "Evening reflection, personal documentation, and quiet rest",
  ];

  const beats: LifeBeat[] = [
    {
      time: "07:00",
      heading: "Morning Briefing & Planning",
      text: `${name} begins the day reviewing updates and organizing the schedule ahead.`,
    },
    {
      time: "09:30",
      heading: "Core Operations",
      text: `Convenes with working partners to address ongoing operational priorities.`,
    },
    {
      time: "14:00",
      heading: "Focused Execution",
      text: `Dedicates uninterrupted hours to hands-on analytical or technical work.`,
    },
    {
      time: "18:30",
      heading: "Community Dialogue",
      text: `Meets with peers and friends to discuss community matters and unwind.`,
    },
    {
      time: "21:30",
      heading: "Night Synthesis",
      text: `Reflects on the accomplishments of the day before taking restful sleep.`,
    },
  ];

  const futures: LifeFuturePath[] = [
    {
      id: "a",
      name: "Institutional Stewardship",
      flavor: "Most natural",
      summary: `Assuming broader administrative leadership across the primary institutions of ${universeName}.`,
      opportunities: ["Significant civic influence", "Lasting institutional legacy", "Wide resource allocation"],
      challenges: ["Bureaucratic complexity", "Less time for direct practice", "Public accountability"],
      consequences: `Solidifies permanent systemic standards that safeguard the community for decades.`,
    },
    {
      id: "b",
      name: "Frontier Pioneer",
      flavor: "Ambitious",
      summary: `Venturing beyond settled territories to establish new methodologies in uncharted frontiers.`,
      opportunities: ["Full operational autonomy", "Novel discoveries", "Pioneering recognition"],
      challenges: ["Uncertain resources", "Distance from established support", "Higher physical risks"],
      consequences: `Expands the horizon of possibility, forging paths for others to follow.`,
    },
    {
      id: "c",
      name: "Reflective Synthesis",
      flavor: "Wildcard",
      summary: `Withdrawing from public administration to author comprehensive treatises and mentor selected pupils.`,
      opportunities: ["Deep intellectual freedom", "Clarity of vision", "Lasting philosophical impact"],
      challenges: ["Diminished immediate authority", "Financial adjustments", "Solitary workload"],
      consequences: `Creates an enduring body of insight that influences future generations.`,
    },
  ];

  const timeline: LifeTimelineEntry[] = [
    {
      age: 14,
      label: "Early Discovery",
      title: "First Spark of Purpose",
      text: "Encountered initial experiences that awakened a lifelong fascination with their field.",
      kind: "origin",
    },
    {
      age: 21,
      label: "Formative Milestone",
      title: "Formal Qualification",
      text: "Completed foundational education and acquired formal credentials.",
      kind: "education",
    },
    {
      age: 27,
      label: "Decisive Fork",
      title: "The Crossroads",
      text: turningPoint,
      kind: "choice",
    },
    {
      age: 32,
      label: "Key Milestone",
      title: "Breakthrough Achievement",
      text: achievements[0] ?? "Earned widespread respect through decisive technical contributions.",
      kind: "achievement",
    },
    {
      age,
      label: "Current Reality",
      title: "Defining Tension",
      text: dilemma,
      kind: "present",
    },
    {
      age: age + 8,
      label: "Upcoming Horizon",
      title: "Approaching Crossroads",
      text: "Positioned to lead a major structural transition across their sphere of practice.",
      kind: "future",
    },
  ];

  const divergences: EarthDivergence[] = [
    {
      id: "d1",
      title: "Divergence in Civic Organization",
      text: `In ${universeName}, societal progression is organized around collaborative practitioner guilds rather than speculative finance.`,
      effects: "Ensures material security and steady mentorship throughout an individual's career.",
    },
    {
      id: "d2",
      title: "Technological Principles",
      text: "Infrastructure and instruments are designed for modular longevity and community repairability.",
      effects: "Reduces disposable consumer cycles and elevates enduring craftsmanship.",
    },
  ];

  const rows: EarthRow[] = EARTH_DIMENSIONS.map((dim) => {
    switch (dim) {
      case "Occupation":
        return {
          dimension: dim,
          here: occupation,
          earth: "Standard modern professional occupation",
          because: `Civic priorities in ${universeName} support specialized demand for ${occupation.toLowerCase()}.`,
          divergenceId: "d1",
        };
      case "Social status":
        return {
          dimension: dim,
          here: "Respected senior practitioner within civic networks",
          earth: "Corporate or institutional ranking",
          because: "Status is earned through verified technical contributions to community welfare.",
          divergenceId: "d1",
        };
      case "Financial circumstances":
        return {
          dimension: dim,
          here: "Equitable access to municipal resources and guild credit",
          earth: "Individual wage dependency and market-driven expenses",
          because: "Essential living provisions are held in common stewardship.",
          divergenceId: "d2",
        };
      case "Relationships":
        return {
          dimension: dim,
          here: "Enduring guild solidarity and close neighborhood bonds",
          earth: "Geographically dispersed social connections",
          because: "Settlement design actively fosters walkable, multi-generational communities.",
          divergenceId: "d1",
        };
      case "Freedoms":
        return {
          dimension: dim,
          here: "Substantial operational autonomy guided by peer trust",
          earth: "External regulatory compliance and corporate metrics",
          because: "Authority is entrusted to accredited practitioners rather than distant administrators.",
          divergenceId: "d1",
        };
      case "Daily life":
        return {
          dimension: dim,
          here: "Measured cadence attuned to community rhythms and daylight",
          earth: "Rapid schedule paced by commute times and continuous digital notifications",
          because: "Urban planning emphasizes local convenience and peaceful shared courtyards.",
          divergenceId: "d2",
        };
      case "Available technology":
        return {
          dimension: dim,
          here: "Durable, modular tools designed for lifetime repairability",
          earth: "Fast-cycle consumer hardware tied to cloud subscriptions",
          because: "Engineering principles prioritize resource conservation and self-reliance.",
          divergenceId: "d2",
        };
      case "Future opportunities":
        return {
          dimension: dim,
          here: "Transparent pathways toward senior mentorship or independent research",
          earth: "Uncertain trajectories influenced by macroeconomic shifts",
          because: "Civic frameworks maintain predictable continuity across decades.",
          divergenceId: "d1",
        };
    }
  });

  const visitorSignals = [
    `Your selected interests provided thematic inspiration for ${name}'s expertise as ${occupation.toLowerCase()}.`,
    `Your declared strengths helped shape ${name}'s collaborative and resilient demeanor.`,
    `Your personal ambition inspired the momentum that propelled this timeline forward.`,
  ];

  return {
    engineVersion: ENGINE_VERSION,
    identity: {
      fullName: name,
      age,
      ageNote: "Standard solar calendar years measured in our physical universe.",
      personalitySummary: personality,
      biography,
    },
    work: {
      occupation,
      expertise: skills,
      careerHistory,
      responsibilities,
      workplace: `Regional operational center in ${universeName}`,
    },
    standing: {
      wealthBracket: "Comfortable / Stable",
      wealthText: "Holds secure access to collective guild resources and regional credit.",
      socialClass: "Respected Practitioner",
      reputation: "Acknowledged for steady judgment and dependable technical leadership.",
      prestige: "Recognized across regional councils and vocational associations.",
      influence: "Direct advisory voice in community planning and resource allocation.",
      influenceLevel: influenceScore,
      economicCircumstances: "Protected from economic precarity through resilient municipal foundations.",
    },
    home: {
      dwelling: "Comfortable district dwelling near central civic pathways",
      environment: "Temperate neighborhood featuring green courtyards and pedestrian lanes.",
      lifestyle: "Steady daily cadence balancing dedicated work with evening contemplation.",
      possessions,
      transport: "Municipal transit system and personal light transport vehicle.",
      routine,
    },
    people: {
      family: "Maintains enduring contact with relatives across neighboring districts.",
      relationships: "Encouraged by an inner circle of trusted associates and lifelong peers.",
      friendships: "Close connections built upon decades of mutual collaboration.",
      socialCircle: "Active participant in guild assemblies and local deliberations.",
    },
    formative: {
      childhood: origin.slice(0, 180) + (origin.length > 180 ? "..." : ""),
      education: "Structured multi-disciplinary studies combined with hands-on guild apprenticeships.",
      decisions: [
        "Dedicated themselves to vocational mastery early in life.",
        "Chose regional community investment over distant corporate prestige.",
      ],
      achievements,
      setbacks: failures,
      turningPoints: [turningPoint],
    },
    society: {
      summary: "A balanced civilization uniting individual enterprise with mutual community care.",
      laws: "Civic statutes emphasizing restorative justice and equitable resource access.",
      culture: "Strong respect for creative craft, honest dialogue, and civic participation.",
      institutions: "Autonomous practitioner guilds and open deliberative assemblies.",
      expectations: "Citizens are welcomed to apply their talents toward shared social health.",
      opportunity: "Accessible avenues for advancement through verified mastery and community service.",
    },
    world: {
      technology: "Advanced modular systems prioritizing longevity, low energy demand, and easy maintenance.",
      science: "Empirical observation integrated with ecological balance and physical constants.",
      geography: "Connected settlements buffered by preserved natural ecosystems.",
      history: "Generations of stable development rooted in cooperative accords.",
      environment: "Carefully stewarded ecological systems in harmonious equilibrium.",
    },
    howDiffers: {
      headline: "A World Driven by Craft and Community Purpose",
      paragraphs: [
        `In ${universeName}, societal institutions prioritize lasting mastery and collective resilience over speculative volatility.`,
        `For ${name}, this creates an environment where work is conducted with calm dignity and personal rhythms are respected.`,
      ],
      keyDifferences: [
        {
          title: "Civic Fellowship",
          text: "Resources are managed collaboratively, shielding residents from sudden economic precarity.",
        },
        {
          title: "Generational Longevity",
          text: "Infrastructure and tools are engineered to endure across generations without planned obsolescence.",
        },
      ],
    },
    dayInLife: {
      title: "A Day in This Life",
      beats,
      closing: `${name} concludes the day with quiet satisfaction, having advanced meaningful commitments in harmony with their values.`,
    },
    futures: {
      title: "Three Possible Futures",
      paths: futures,
      disclaimer: "These forward trajectories represent speculative story branches based on current momentum, not immutable predictions.",
    },
    timeline,
    earthComparison: {
      basis: "general-reference",
      basisNote: "This Earth reference reflects typical modern conditions for comparison, because your questionnaire contained no private personal details.",
      rows,
      divergences,
      visitorSignals,
    },
  };
}
